import ajax from '../../common/services/httpService';

/**
 * Normalize whatever axios / proxies return into a plain array of row objects.
 * @param {*} raw
 * @returns {Array<Record<string, unknown>>}
 */
function coerceSiteCommunicationList(raw) {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  if (raw && typeof raw === 'object') {
    if (Array.isArray(raw.entries)) {
      return raw.entries;
    }
    if (Array.isArray(raw.data)) {
      return raw.data;
    }
  }
  return [];
}

/**
 * Get communication log entries for a site (chronological, with author and timestamp).
 * @param {string|number} siteId - Site ID
 * @returns {Promise<Array<{ id: number; siteId: number; userId: number; content: string; createdAt: string; authorName?: string; authorEmail?: string }>>}
 */
export async function getEntries(siteId) {
  const raw = await ajax.get(`site-communication/${siteId}`);
  return coerceSiteCommunicationList(raw);
}

/**
 * Get a single entry by id. Returns entry (with site_id) only if user has access to that site. Used for share links.
 * @param {string|number} entryId - Entry ID
 * @returns {Promise<{ id: number; site_id: number; content: string; type: string; is_pinned: number; created_at: string; author_name: string; author_email: string }>}
 */
export async function getEntryById(entryId) {
  const entry = await ajax.get(`site-communication/entry/${entryId}`);
  return entry;
}

/**
 * Add a new entry to the site communication log.
 * @param {string|number} siteId - Site ID
 * @param {{ content: string; type?: string }} payload - content (required), type (optional: general|update|issue|follow_up|meeting)
 * @returns {Promise<Object>} Created entry
 */
export async function addEntry(siteId, { content, type }) {
  const body = { siteId, content, type: type || 'general' };
  const created = await ajax.post('site-communication', body);
  return created;
}

/**
 * Pin or unpin a note. Max 2 pinned per site (oldest pinned is auto-unpinned).
 * @param {string|number} siteId - Site ID
 * @param {string|number} entryId - Entry ID
 * @param {boolean} pinned - true to pin, false to unpin
 * @returns {Promise<{ entry: Object; autoUnpinnedEntry: Object | null }>}
 */
export async function setPinned(siteId, entryId, pinned) {
  const body = await ajax.patch(`site-communication/${siteId}/${entryId}/pin`, { pinned });
  return body;
}

/**
 * Update an entry's content and/or type. Only the author can edit.
 * @param {string|number} siteId - Site ID
 * @param {string|number} entryId - Entry ID
 * @param {{ content: string; type?: string }} payload - content (required), type (optional)
 * @returns {Promise<Object>} Updated entry
 */
export async function updateEntry(siteId, entryId, { content, type }) {
  const updated = await ajax.put(`site-communication/${siteId}/${entryId}`, { content, type });
  return updated;
}

/**
 * Delete an entry. Only the author can delete.
 * @param {string|number} siteId - Site ID
 * @param {string|number} entryId - Entry ID
 * @returns {Promise<void>}
 */
export async function deleteEntry(siteId, entryId) {
  await ajax.delete(`site-communication/${siteId}/${entryId}`);
}
